const { types } = require('@showrunner/codex')
const { parser } = require('./utils')

const markRE = /(_|\*{1,2})/g
const PAGE_BREAKS = ['page_break', 'title_page_break']
const DUAL_DIALOGUE_TYPES = [
  'dual_dialogue_begin',
  'dual_dialogue_end'
]
const DIALOGUE_TYPES = [
  'dialogue_begin',
  'dialogue_end'
]

/**
 * convenience method for creating a new page object.
 * @returns {object} prose page
 */
function createPage () {
  return {
    type: types.PAGE,
    attrs: { dynamic: false },
    content: []
  }
}

/**
 * Reducer to add element to current page in state.
 * @param {object} el - element to add
 * @param {object} state - accumulator state
 * @returns {object} updated state
 */
function addToPage (el, state) {
  const pageIndex = state.doc.content.length - 1

  // guard against malformed fountain content
  if (el) state.doc.content[pageIndex].content.push(el)

  return state
}

/**
 * Reducer to add element to current dual dialogue column in state.
 * @param {object} state - accumulator state
 * @returns {object} updated state
 */
function addToDual (el, state) {
  const dualIndex = state.dual.content.length - 1

  state.dual.content[dualIndex].content.push(el)

  return state
}

/**
 * Convert an array of parsed fountain tokens into a prose JSON document.
 * @param {array} tokens - parsed fountain tokens
 * @returns {object} prose document in JSON format
 */
function prosify (tokens) {
  const { doc } = tokens.reduce((state, token) => {
    // add a page if the token is a page break
    if (PAGE_BREAKS.includes(token.type)) {
      state.doc.content.push(createPage())
      return state
    }

    if (DUAL_DIALOGUE_TYPES.includes(token.type)) {
      if (token.type === 'dual_dialogue_begin') {
        state.dual = {
          type: types.DUAL_DIALOGUE,
          content: []
        }
      }
      if (token.type === 'dual_dialogue_end') {
        addToPage(state.dual, state)
        state.dual = null
      }
      return state
    }

    if (DIALOGUE_TYPES.includes(token.type)) {
      if (state.dual && token.type === 'dialogue_begin') {
        state.dual.content.push({
          type: types.DUAL_DIALOGUE_COLUMN,
          content: []
        })
      }
      return state
    }

    // create new element
    const el = createProseElement(token)

    // add new element to current location
    if (state.dual) {
      state = addToDual(el, state)
    } else {
      state = addToPage(el, state)
    }

    // return doc for reduce function
    return state
  }, {
    doc: {
      // starting doc
      type: types.DOC,
      // an empty page is a valid doc
      content: [createPage()]
    }
  })

  return doc
}

/**
 * Create a prose element object from a fountain token.
 * @param {object} token - parsed fountain token
 * @returns {object} prose element
 */
function createProseElement (token) {
  const el = {
    // NOTE: debug info
    parsedType: token.type
  }

  switch (token.type) {
    case 'character':
      el.type = types.CHARACTER
      break
    case 'dialogue':
      el.type = types.DIALOGUE
      break
    case 'parenthetical':
      el.type = types.PARENTHETICAL
      break
    case 'scene_heading':
      el.type = types.SCENE_HEADING
      break
    case 'transition':
      el.type = types.TRANSITION
      break
    // fallback to ACTION for all other token types
    default:
      el.type = types.ACTION
  }

  // NOTE: empty text nodes are invalid in prosemirror
  if (token.text && token.text !== '') {
    el.content = parseFountainText(token.text)
  }

  return el
}

/**
 *
 * @param {string} raw - raw text string
 * @return {array} text nodes with marks
 */
function parseFountainText (raw) {
  // dont format multiline comments or manually declared revision lines
  const shortcircuit = /(\/\*)/g.test(raw) || /\\/g.test(raw)

  if (shortcircuit) {
    return [{
      text: raw,
      type: types.TEXT
    }]
  }

  const block = raw.split(markRE).reduce((acc, cur) => {
    let markChanged = false
    // underline
    if (cur === '_') {
      acc.marks.underline = !acc.marks.underline
      markChanged = true
    }
    if (cur === '*') {
      // em
      acc.marks.em = !acc.marks.em
      markChanged = true
    }
    if (cur === '**') {
      // strong
      acc.marks.strong = !acc.marks.strong
      markChanged = true
    }
    if (markChanged) {
      if (acc.content.text === '') {
        acc.content.marks = Object.keys(acc.marks)
          .filter(m => acc.marks[m])
          .map(type => ({ type }))
      } else {
        acc.content.push({
          marks: Object.keys(acc.marks)
            .filter(m => acc.marks[m])
            .map(type => ({ type })),
          text: ''
        })
      }
    } else {
      acc.content[acc.content.length - 1].text += cur
    }
    return acc
  }, {
    marks: {
      underline: false,
      em: false,
      strong: false
    },
    content: [{ text: '', marks: [] }]
  })

  return block.content
    .filter(node => node.text !== '')
    .map(node => {
      node.type = 'text'
      if (node.marks.length === 0) delete node.marks
      return node
    })
}

/**
 * Convert fountain plaintext to a prose JSON document.
 * @param {string} script - fountain text
 * @returns {object} prose JSON document
 */
const fountainToProse = function (script) {
  return parser(script, prosify, true)
}

module.exports = {
  fountainToProse,
  parseFountainText
}
